<template>
    <CartTemplate :canBack="!isSourceSale" :hasAlert="isSourceSale" :isPlan="isPlanPath">
        <template #configuration>
            <!-- Navigation Tabs -->
            <CartNavigation
                :smallMargin="isSourceSale"
                context="client"
                data-test="cart-navigation"
            />

            <!-- EXPIRATION MESSAGE -->
            <expiration-message
                v-if="isSourceSale && !isPaymentPath"
                :expirationDate="expirationDate"
                data-test="expiration-message"
            ></expiration-message>
            <!-- Tabs Form -->
            <el-form ref="clientCartForm" labelPosition="top" :rules="setRules" :model="modelForm">
                <router-view store="client"></router-view>
            </el-form>
        </template>
        <!-- Summary -->
        <template #resume>
            <CartSummary context="client">
                <template #licences>
                    <ul class="ov-auto mb-0 cart-recap-products">
                        <li
                            v-for="(product, index) in productsWithLicences"
                            ref="products-summary"
                            :key="product.code"
                            data-test="product-summary"
                            class="flex-row"
                            :class="{ 'mb-2': index + 1 !== productsWithLicences.length }"
                        >
                            <div>
                                <p ref="product-quantity-summary" class="grey m-0 fs-3">
                                    {{
                                        $l10n.n__(
                                            '%(total)s licence %(productsName)s',
                                            '%(total)s licences %(productsName)s',
                                            products[product.id].quantity,
                                            {
                                                total: products[product.id].quantity,
                                                productsName: product.title,
                                            },
                                        )
                                    }}
                                </p>
                                <span
                                    v-if="freeLicencesByProduct(product.id)"
                                    ref="free-quantity-summary"
                                    class="grey"
                                    >{{
                                        $l10n.n__(
                                            'Dont %s offerte',
                                            'Dont %s offertes',
                                            freeLicencesByProduct(product.id),
                                            freeLicencesByProduct(product.id),
                                        )
                                    }}</span
                                >
                            </div>
                            <template v-if="products[product.id].total">
                                <p
                                    v-if="freeLicencesByProduct(product.id)"
                                    ref="product-discount-total-summary"
                                    class="flex-item-right my-0"
                                >
                                    <span class="strike grey semibold">
                                        {{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly.total_amount,
                                            )
                                        }}
                                    </span>
                                    <span class="success-50 fs-3 pl-2 semibold">
                                        {{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly
                                                    .total_amount_discounted,
                                                numformat,
                                                true,
                                            )
                                        }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                    </span>
                                </p>
                                <p
                                    v-else
                                    ref="product-total-summary"
                                    class="flex-item-right my-0 fs-3 semibold grey"
                                >
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                        <!-- Packs Summary -->
                        <li
                            v-for="(product, index) in [
                                ...packsWithLicences,
                                ...groupedPacksWithLicences,
                            ]"
                            :key="product.code"
                            ref="packs-summary"
                            class="flex-row"
                            data-test="packs-summary"
                            :class="{
                                'mb-2':
                                    index + 1 !==
                                    [...packsWithLicences, ...groupedPacksWithLicences].length,
                                'mt-2': index === 0,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.__('Module %(productsName)s', {
                                            productsName: !!product.groups.length
                                                ? product.printableName
                                                : product.title,
                                        })
                                    }}
                                </p>
                            </div>
                            <template v-if="products[product.id].total">
                                <!-- Discount detail -->
                                <p
                                    v-if="
                                        products[product.id].total.monthly.total_amount_discounted <
                                        products[product.id].total.monthly.total_amount
                                    "
                                    ref="packs-total-discount-summary"
                                    class="flex-item-right my-0 semibold"
                                >
                                    <span class="strike grey semibold">
                                        {{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly.total_amount,
                                                numformat,
                                                true,
                                            )
                                        }}
                                    </span>
                                    <span class="success-50 fs-3 pl-2">
                                        <template
                                            v-if="
                                                products[product.id].total.monthly
                                                    .total_amount_discounted !== 0
                                            "
                                        >
                                            {{
                                                $filters.numberFormatToFloatWithOptions(
                                                    products[product.id].total.monthly
                                                        .total_amount_discounted,
                                                    numformat,
                                                    true,
                                                )
                                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                        </template>
                                        <template v-else>{{ $l10n.__('Offert') }}</template>
                                    </span>
                                </p>
                                <!-- Base price -->
                                <p
                                    v-else
                                    ref="packs-total-summary"
                                    class="flex-item-right my-0 fs-3 semibold grey"
                                >
                                    *
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                    </ul>
                </template>
                <template #subtotal>
                    <div class="flex-column fs-3 cart-recap-total-label">
                        <span class="mb-2">{{ $l10n.__('Sous-total') }}</span>
                        <span v-if="taxRate && taxRate !== 0" class="mb-2"
                            >{{ $l10n.__('TVA') }} {{ taxRate }}%</span
                        >
                    </div>
                    <div
                        v-if="isYearly"
                        ref="subtotal-summary-yearly"
                        class="flex-column aright fs-3"
                    >
                        <span class="semibold mb-2">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span>
                        </span>
                        <span v-if="taxRate && taxRate !== 0" class="semibold mb-2">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                    <div
                        v-if="isMonthly"
                        ref="subtotal-summary-monthly"
                        class="flex-column aright fs-3"
                    >
                        <span class="semibold mb-2">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                        </span>
                        <span v-if="taxRate && taxRate !== 0" class="semibold mb-2">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                </template>
                <template #total>
                    <div
                        ref="summary-total"
                        class="flex-row jc-space"
                        :class="{ 'mb-4': !isPaymentPath }"
                    >
                        <p class="fs-4 my-0 bold cart-recap-total-label">
                            {{ $l10n.__('Total TTC') }}
                        </p>
                        <p v-if="isYearly" class="fs-4 my-0 aright bold">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span>
                        </p>
                        <p v-if="isMonthly" class="fs-4 my-0 aright bold">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                        </p>
                    </div>
                </template>
                <template #actions>
                    <template v-if="isPaymentPath">
                        <span class="separator block my-3"></span>
                        <div class="flex-row terms mb-4">
                            <el-checkbox v-model="cguv"> </el-checkbox>
                            <span class="ml-2 fs-3" v-html="cguvLink"></span>
                        </div>
                    </template>
                    <CompassButton
                        ref="form-submit"
                        :label="labelButton"
                        type="primary"
                        :disabled="(!cguv && isPaymentPath) || computeLoading"
                        :loading="computeLoading"
                        class="wp-10"
                        data-test="form-submit-button"
                        @click="submitForm('clientCartForm', 'nextAction')"
                    />
                </template>
            </CartSummary>
        </template>
    </CartTemplate>
</template>

<script>
import PricingV4Module from 'stores/sellsyaccountmanager/v4/client';
import { createNamespacedHelpers } from 'vuex';
import CartTemplate from 'vcomponents/pages/account/CartTemplate.vue';
import CartSummary from 'vcomponents/pages/account/CartSummary.vue';
import { fetchCart, fetchMetadata } from 'api/sellsyaccountmanager';
import FormMixin from 'mixins/FormMixins';
import AddressRules from 'vcomponents/forms/rules/AddressRules.js';
import VatIdentifierValidator from 'Services/VatIdentifierValidator';
import { checkIsTrue } from 'vcomponents/forms/rules/Rules';
import { goToUrl } from 'js/vue/helpers';
import CgvuApi from 'api/terms/cgvu.js';
import { legalTermsPath, getGoSellsyUrl } from 'Config/authenticated/goSellsyPaths';
import Loader from 'vcomponents/utilities/Loader.vue';
import ExpirationMessage from 'vcomponents/pages/account/client/ExpirationMessage.vue';
import CartNavigation from './CartNavigation.vue';

const clientCart = createNamespacedHelpers('clientCart');
const user = createNamespacedHelpers('user');
export default {
    mixins: [FormMixin],
    data() {
        return {
            rules: {
                name: [
                    {
                        validator: (rule, value, callback, source, options) => {
                            const contact = {
                                name: this.modelForm.name,
                                forename: this.modelForm.forename,
                                email: this.modelForm.email,
                                phone: this.modelForm.phone,
                            };

                            if (_.some(contact, (info) => _.isNil(info) || !info.length))
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez renseigner des informations de contact valides',
                                        ),
                                    ),
                                );
                            callback();
                        },
                    },
                ],
                forename: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner un prénom') },
                ],
                phone: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone'),
                    },
                    {
                        min: 4,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone valide'),
                    },
                ],
                email: [
                    {
                        required: true,
                        type: 'email',
                        message: this.$l10n.__('Veuillez renseigner une adresse email'),
                    },
                ],
                products: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Cart must get 2 paid licences or 1 if allowed by permission
                             */
                            if (this.customer.allowsOneLicense && this.quantityPaidLicences < 1) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 1 licence payante',
                                        ),
                                    ),
                                );
                            } else if (
                                !this.customer.allowsOneLicense &&
                                this.quantityPaidLicences < 2
                            ) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 2 licences payantes',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                billingSettings: {
                    ...AddressRules,
                },
                billingLocale: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner une langue de facturation'),
                    },
                ],
                vatIntra: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Check TVA Intra format with associated country, but no require.
                             */
                            if (
                                value === '' ||
                                _.isNil(value) ||
                                _.isUndefined(this.billingSettings.countryCode)
                            )
                                return callback();

                            if (_.isNull(this.billingSettings.countryCode))
                                return callback(new Error(this.$l10n.__('Aucun pays sélectionné')));

                            const validator = new VatIdentifierValidator(
                                this.billingSettings.countryCode,
                            );
                            if (!validator.isValid(value)) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Le format du n° de tva ne correspond pas à celui du pays sélectionné',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                cardName: [
                    {
                        required: false,
                        message: this.$l10n.__('Veuillez renseigner le nom sur la carte'),
                    },
                ],
                billingContactPayerEmail: [
                    {
                        type: 'email',
                        message: this.$l10n.__('Veuillez renseigner une adresse email valide'),
                        trigger: ['blur'],
                    },
                ],
            },
            cardValidator: {
                cardNumber: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un numéro de carte valide'),
                    },
                ],
                cardExpiry: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner une date valide'),
                    },
                ],
                cardCvc: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un code valide'),
                    },
                ],
                cardName: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner le nom sur la carte'),
                    },
                ],
            },
        };
    },
    computed: {
        ...clientCart.mapGetters([
            'isSourceSale',
            'expirationDate',
            'modelForm',
            'recurrency',
            'products',
            'isYearly',
            'isMonthly',
            'productsWithLicences',
            'packsWithLicences',
            'groupedPacksWithLicences',
            'computeLoading',
            'freeLicencesByProduct',
            'totalYearly',
            'totalMonthly',
            'getLocalStorage',
            'labelPlan',
            'canGoToPayment',
            'cguvValidated',
            'cguvVersion',
            'termsType',
            'cguvAlreadyValidated',
            'billingSettings',
            'isManualMethod',
            'isBillingContactPayer',
            'cartId',
            'taxRate',
            'numformat',
            'customerId',
            'context',
            'quantityPaidLicences',
            'customer',
        ]),
        ...user.mapState(['locale']),
        setRules() {
            if (!this.isBillingContactPayer) {
                return Object.assign({ ...this.rules }, this.cardValidator);
            } else {
                return this.rules;
            }
        },
        isPlanPath() {
            return this.$route.name === 'sam.v4.client.form.plan';
        },
        isConfigurationPath() {
            return this.$route.name === 'sam.v4.client.form.configuration';
        },
        isPaymentPath() {
            return this.$route.name === 'sam.v4.client.form.payment';
        },
        cguv: {
            get() {
                return this.cguvValidated;
            },
            set(value) {
                return this.setCguvStatus(value);
            },
        },
        legalInformationLang() {
            const acceptedLang = ['en', 'fr'];
            const userLocale = this.locale.split('_')[0];
            return acceptedLang.includes(userLocale) ? userLocale : 'fr';
        },
        cguvLink() {
            return this.$l10n.__(
                'En cochant cette case, je reconnais avoir pris connaissance des  %(startLink)s Conditions Générales de Vente et d’Utilisation %(endLink)s de Sellsy et je les accepte.',
                {
                    startLink: `<a target='_blank' class='fs-3 semibold' href='${getGoSellsyUrl(
                        this.legalInformationLang,
                        legalTermsPath,
                    )}'>`,
                    endLink: `</a>`,
                },
            );
        },
        labelButton() {
            return this.isPaymentPath
                ? this.isManualMethod || this.isBillingContactPayer
                    ? this.$l10n.__('Valider la souscription')
                    : this.$l10n.__('Valider le paiement')
                : this.$l10n.__('Continuer');
        },
        nextRoute() {
            const actualRoute = this.$route.name;
            switch (actualRoute) {
                case 'sam.v4.client.form.plan':
                    return 'sam.v4.client.form.configuration';
                case 'sam.v4.client.form.configuration':
                    return 'sam.v4.client.form.payment';
                default:
                    return false;
            }
        },
        /**
         * Set the class name for the underline's effect in nav
         */
        selectRoute() {
            if (this.isPlanPath) return 'plan';
            if (this.isConfigurationPath) return 'config';
            if (this.isPaymentPath) return 'payment';
        },
    },
    created() {
        this.$store.registerModule('clientCart', PricingV4Module);
        this.setSource('customer');
        this.setCustomerId(this.$store.state.corp.customer);
        this.setUserType(true);
        this.setCorporationId(this.$store.state.corp.id);
        this.setUserContext(this.$route.meta.user_context);

        if (this.$store.state.corp.accountLevel === 'gold')
            return this.$router.push({
                name: 'sam.v4.client.renew.form.plan',
                params: { renewCartId: 'new' },
            });

        fetchMetadata(this.customerId, this.context, this.termsType).then((res) => {
            this.setMetadata(res);
            if (!_.isNil(this.getLocalStorage) && _.isNil(res.cart.id)) {
                const cart = this.getLocalStorage;
                if (cart.customerId === this.customerId) {
                    this.updatePlan(cart.plan);
                    this.updateRecurrency(cart.recurrency);
                    this.setProducts(cart.products);
                }
            }
            this.computeCart();
        });

        EventBus.on('PlanCart:submitSelectedPlan', this.nextAction);
    },
    unmounted() {
        EventBus.off('PlanCart:submitSelectedPlan', this.nextAction);
    },
    methods: {
        ...clientCart.mapMutations([
            'setSource',
            'setCustomerId',
            'setProducts',
            'setCguvStatus',
            'setComputeLoading',
            'setCorporationId',
            'setUserContext',
            'setUserType',
        ]),
        ...clientCart.mapActions([
            'fetchMetadata',
            'updateProductQuantity',
            'updateRecurrency',
            'updatePlan',
            'computeCart',
            'setCart',
            'setMetadata',
            'updateCart',
            'createCart',
        ]),
        nextAction() {
            if (this.nextRoute) this.continue();
            else this.create();
        },
        continue() {
            this.$router.push({ name: this.nextRoute });
        },
        async create() {
            try {
                await CgvuApi.acceptTerms(this.termsType, this.cguvVersion);
                // If doesn't have id, its a new cart
                if (_.isNil(this.cartId)) await this.createCart();
                else await this.updateCart(this.cartId);

                if (this.isManualMethod || this.isBillingContactPayer)
                    return goToUrl(
                        this.$router.resolve({ name: 'sam.subscription.payment-confirmed' }).href,
                    );
                EventBus.emit('stripe:create');
            } catch (err) {
                this.setComputeLoading(false);
                Push.error(
                    this.$l10n.__('Une erreur est survenue, veuillez ré-essayer ultérieurement.'),
                );
            }
        },
    },
    beforeUnmount() {
        this.$store.unregisterModule('clientCart');
    },
    /**
     * Get cart by UUID in params.
     * Configure cart or redirect to error before render component
     */
    async beforeRouteEnter(to, from, next) {
        if (to.params.id !== 'new') {
            await fetchCart(to.params.id, { user_context: to.meta.user_context })
                .then((response) => {
                    if (
                        response.source === 'sales' &&
                        to.name !== 'sam.v4.client.form.configuration'
                    )
                        next({
                            name: 'sam.v4.client.form.configuration',
                            params: { id: to.params.id },
                        });
                    if (response.context === 'upsale')
                        next({
                            name: 'sam.v4.client.upsell.form.plan',
                            params: { upsellCartId: to.params.id },
                        });
                    if (['renew', 'legacy_renew'].includes(response.context))
                        next({
                            name: 'sam.v4.client.renew.form.plan',
                            params: { renewCartId: to.params.id },
                        });
                    next((vm) => {
                        vm.setCart(response);
                    });
                })
                .catch((err) => next(false));
        } else {
            next();
        }
    },
    components: {
        CartTemplate,
        CartSummary,
        Loader,
        ExpirationMessage,
        CartNavigation,
    },
};
</script>

<style lang="scss" scoped>
.cart-licences-list {
    li {
        margin-bottom: 8px;
        &:last-child {
            margin-bottom: 0;
        }
    }
}

.terms {
    :deep(.el-checkbox__input) {
        padding-top: 3px;
    }
}
</style>
